import React, { useState } from "react";
import { useStatsFetch } from "./useStatsApi";
import {
  SectionCard,
  RefreshButton,
  ErrorMessage,
  SimpleTable,
  Badge,
  formatNumber,
} from "./StatComponents";

const USER_TYPE_LABELS = {
  2: "Veli",
  10: "Personel",
  50: "Sürücü",
  1000: "Admin",
};

const userTypeLabel = (usertype) => USER_TYPE_LABELS[String(usertype)] || String(usertype);

const DuplicatePhones = () => {
  const { data, isLoading, error, load } = useStatsFetch("/admin/statistics/duplicate-phones", false);
  const [expandedPhone, setExpandedPhone] = useState(null);

  return (
    <SectionCard
      title={
        data
          ? `Mükerrer Telefon Numaraları (${formatNumber(data.duplicateCount)} numara)`
          : "Mükerrer Telefon Numaraları"
      }
      actions={<RefreshButton onClick={() => load()} isLoading={isLoading} label={data ? "Yenile" : "Hesapla"} />}
    >
      <ErrorMessage message={error} />
      {!data && !isLoading && (
        <p style={{ color: "#999", margin: 0 }}>
          Tüm kullanıcılar taranır, boşluklar yok sayılarak aynı numarayı paylaşan kayıtlar listelenir. Hesaplamak
          için butona basın.
        </p>
      )}
      {isLoading && <p style={{ color: "#666", margin: 0 }}>Hesaplanıyor, bu işlem biraz sürebilir...</p>}
      {data && (
        <>
          {data.parentsWithoutRelations?.length > 0 && (
            <div
              style={{
                padding: "10px 14px",
                background: "#fff3e0",
                border: "1px solid #ffb74d",
                borderRadius: "6px",
                fontSize: "13px",
                marginBottom: "15px",
              }}
            >
              <strong>{formatNumber(data.parentsWithoutRelations.length)}</strong> mükerrer gruptaki veli kaydının
              hiç ilişkisi yok (silinmeye aday olabilir).
            </div>
          )}
          <SimpleTable
            columns={[
              { key: "phone", label: "Telefon" },
              { key: "count", label: "Kayıt", align: "right", render: (row) => formatNumber(row.count) },
              {
                key: "totalRelations",
                label: "Toplam İlişki",
                align: "right",
                render: (row) => formatNumber(row.totalRelations),
              },
              {
                key: "users",
                label: "Kullanıcılar",
                render: (row) => {
                  const isExpanded = expandedPhone === row.phone;
                  return (
                    <div>
                      <button
                        onClick={() => setExpandedPhone(isExpanded ? null : row.phone)}
                        style={{
                          background: "none",
                          border: "1px solid #ccc",
                          padding: "3px 8px",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        {isExpanded ? "Gizle" : "Detay"}
                      </button>
                      {isExpanded && (
                        <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "4px" }}>
                          {row.users.map((u) => (
                            <div key={u.userId} style={{ fontSize: "12px", display: "flex", gap: "6px", alignItems: "center" }}>
                              <Badge color={u.relations === 0 ? "#ffebee" : "#e8f5e9"}>
                                {formatNumber(u.relations)} ilişki
                              </Badge>
                              <Badge>{userTypeLabel(u.usertype)}</Badge>
                              <span>{u.name}</span>
                              <span style={{ color: "#999" }}>{u.userId}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                },
              },
            ]}
            rows={data.duplicates}
            emptyText="Mükerrer telefon numarası bulunamadı."
            rowKey={(row) => row.phone}
          />
        </>
      )}
    </SectionCard>
  );
};

const UnusedCompanies = () => {
  const { data, isLoading, error, load } = useStatsFetch("/admin/statistics/unused-companies", false);

  return (
    <SectionCard
      title={
        data
          ? `Referanssız Şirketler (${formatNumber(data.unusedCount)} / ${formatNumber(data.totalCompanies)})`
          : "Referanssız Şirketler"
      }
      actions={<RefreshButton onClick={() => load()} isLoading={isLoading} label={data ? "Yenile" : "Hesapla"} />}
    >
      <ErrorMessage message={error} />
      {!data && !isLoading && (
        <p style={{ color: "#999", margin: 0 }}>
          Hiçbir koleksiyonda (okul, kişi, öğrenci, sözleşme vb.) verisi olmayan şirketleri bulur.
        </p>
      )}
      {isLoading && <p style={{ color: "#666", margin: 0 }}>Hesaplanıyor, bu işlem biraz sürebilir...</p>}
      {data && (
        <SimpleTable
          columns={[
            { key: "name", label: "Şirket" },
            { key: "id", label: "ID", render: (row) => <span style={{ color: "#999", fontSize: "12px" }}>{row.id}</span> },
            {
              key: "status",
              label: "Durum",
              render: (row) => (
                <span style={{ display: "flex", gap: "6px" }}>
                  <Badge color={row.isActive ? "#e8f5e9" : "#ffebee"}>{row.isActive ? "Aktif" : "Pasif"}</Badge>
                  {row.isDemo && <Badge color="#fff3e0">Demo</Badge>}
                </span>
              ),
            },
          ]}
          rows={data.unused}
          emptyText="Referanssız şirket yok."
          rowKey={(row) => row.id}
        />
      )}
    </SectionCard>
  );
};

const UnusedSchools = () => {
  const { data, isLoading, error, load } = useStatsFetch("/admin/statistics/unused-schools", false);

  return (
    <SectionCard
      title={
        data
          ? `Referanssız Okullar (${formatNumber(data.unusedCount)} / ${formatNumber(data.totalSchools)})`
          : "Referanssız Okullar"
      }
      actions={<RefreshButton onClick={() => load()} isLoading={isLoading} label={data ? "Yenile" : "Hesapla"} />}
    >
      <ErrorMessage message={error} />
      {!data && !isLoading && (
        <p style={{ color: "#999", margin: 0 }}>
          Hiçbir koleksiyonda (öğrenci, servis, sözleşme, vardiya vb.) verisi olmayan okulları bulur.
        </p>
      )}
      {isLoading && <p style={{ color: "#666", margin: 0 }}>Hesaplanıyor, bu işlem biraz sürebilir...</p>}
      {data && (
        <SimpleTable
          columns={[
            { key: "name", label: "Okul" },
            { key: "company", label: "Şirket" },
            { key: "id", label: "ID", render: (row) => <span style={{ color: "#999", fontSize: "12px" }}>{row.id}</span> },
          ]}
          rows={data.unused}
          emptyText="Referanssız okul yok."
          rowKey={(row) => row.id}
        />
      )}
    </SectionCard>
  );
};

const DataHealthSection = () => (
  <div>
    <p style={{ color: "#666", fontSize: "13px", marginTop: 0, marginBottom: "20px" }}>
      Bu bölümdeki sorgular tüm veritabanını taradığı için isteğe bağlı çalıştırılır. Hiçbir veri değiştirilmez,
      sadece okuma yapılır.
    </p>
    <DuplicatePhones />
    <UnusedCompanies />
    <UnusedSchools />
  </div>
);

export default DataHealthSection;
